import { envVars } from '@repo/sdk'
import { init, tanstackRouterBrowserTracingIntegration } from '@sentry/react'
import type { AnyRouter } from '@tanstack/react-router'

/**
 * Sentry is **dormant by default**. Production builds run it only when a DSN is
 * provided; dev (`pnpm dev`) always skips Sentry regardless of env values.
 *
 * ## Enable error tracking
 *
 * 1. Create a project on sentry.io → Settings → Client Keys (DSN) → copy.
 * 2. Set `VITE_SENTRY_DSN=https://...` in the production env
 *    (CI secrets, `.env.production`, hosting platform env, ...).
 * 3. Rebuild. `initSentry` runs and reports errors + performance traces.
 *
 * Verify locally: put the DSN in `.env.local`, run `pnpm build && pnpm preview`,
 * trigger an error (`throw new Error('test')`), check the Sentry dashboard.
 *
 * ## Enable source map upload (optional, recommended)
 *
 * Without this, stack traces in Sentry show minified code. With it, they map
 * back to the original TypeScript source.
 *
 * Set on the CI build job (not in `.env.example` — these are secrets):
 *   - `SENTRY_AUTH_TOKEN` — Sentry → Settings → Auth Tokens (scope: `project:releases`)
 *   - `SENTRY_ORG` — your Sentry org slug
 *   - `SENTRY_PROJECT` — your Sentry project slug
 *
 * The `@sentry/vite-plugin` in `vite.config.ts` auto-disables when the token
 * is missing, so local builds stay clean. Release name is auto-detected from
 * git by the plugin — no `VITE_SENTRY_RELEASE` env var needed.
 *
 * ## Customize
 *
 * Extend integrations (Session Replay, Profiling, Feedback widget) inside
 * `init({ integrations: [...] })` below. Tune `tracesSampleRate` per traffic
 * volume (0.1 = 10% of transactions sampled).
 *
 * @see https://docs.sentry.io/platforms/javascript/guides/react/
 */
export function initSentry(router: AnyRouter) {
	if (!import.meta.env.PROD) {
		return
	}

	const dsn = envVars.VITE_SENTRY_DSN
	if (!dsn) {
		return
	}

	init({
		dsn,
		integrations: [tanstackRouterBrowserTracingIntegration(router)],
		tracesSampleRate: 0.1,
		ignoreErrors: [
			'ResizeObserver loop limit exceeded',
			'Non-Error promise rejection captured'
		]
	})
}
